import { confirmNotification, warningNotification } from "@app/common/components/notifications";
import {
  formattedDataSelect,
  formatUpdatedAt,
  UseFormattedDataSelect
} from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import {
  useGetCompanyClientList,
  useGetContractList,
  useGetExtensionList
} from "@app/services/common/hooks";
import {
  useCreateSupport,
  useGetSupportParamsList,
  useUpdateSupport
} from "@app/services/supportParams/hooks";
import { Extension, Support, SupportParamsFilter } from "@app/services/supportParams/types";
import { generateId } from "@app/utils/generateId";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Menu,
  MultiSelect,
  Select,
  TextInput,
  Title,
  Tooltip
} from "@mantine/core";
import {
  IconChevronLeft,
  IconCircleCheck,
  IconCircleFilled,
  IconCirclePlus,
  IconCircleX,
  IconDotsVertical,
  IconPencil,
  IconTrash
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";
import { Fragment } from "react/jsx-runtime";

type SupportUser =
  | string
  | {
      id?: number;
      firstName?: string;
      firstSurname?: string;
      first_name?: string;
      first_surname?: string;
    }
  | null
  | undefined;

const HandleUpdateSupportParameter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState<SupportParamsFilter>({
    corporateClient: null,
    contractId: null,
    trigger: 0
  });
  const action = location.state.action;
  const { can } = usePermissions();
  const canCreate = can(RBAC_ACTIONS.CREATE, MODULE_PERMISSION_PREFIX.TM_CONTRACT_SUPPORTS);
  const canEdit = can(RBAC_ACTIONS.EDIT, MODULE_PERMISSION_PREFIX.TM_CONTRACT_SUPPORTS);
  const canDelete = can(RBAC_ACTIONS.DELETE, MODULE_PERMISSION_PREFIX.TM_CONTRACT_SUPPORTS);
  const { contractId, corporateClient } = location.state.data ?? {};
  const [supportListUpdated, setSupportListUpdated] = useState<Support[]>([]);

  const {
    data: supportList = {
      results: [],
      rowTotal: 0
    }
  } = useGetSupportParamsList(
    {
      ...filters
    },
    filters.contractId !== null
  );

  const {
    data: companyList = {
      results: [],
      rowTotal: 0
    }
  } = useGetCompanyClientList({
    status: "enabled",
    eaccount: GCidAccount()
  });

  const {
    data: extensionList = {
      results: [],
      rowTotal: 0
    }
  } = useGetExtensionList();

  const {
    data: contractList = {
      results: [],
      rowTotal: 0
    }
  } = useGetContractList(
    {
      corporateClient: Number(filters.corporateClient),
      slim: 1
    },
    filters.corporateClient !== null
  );

  const isDisabledField = (support: Support) => {
    if (action === "create") {
      const isDisabled = support.id;
      return Boolean(isDisabled);
    }
    return support.isEditing ? false : true;
  };

  const formatSupportUser = (user: SupportUser) => {
    if (!user) {
      return "";
    }

    if (typeof user === "string") {
      return user;
    }

    const firstName = user.firstName ?? user.first_name ?? "";
    const firstSurname = user.firstSurname ?? user.first_surname ?? "";
    return `${firstName} ${firstSurname}`.trim();
  };

  useEffect(() => {
    if (action !== "create") {
      setFilters({
        contractId,
        corporateClient
      });
    }
  }, [contractId, corporateClient, action]);

  useEffect(() => {
    if (supportList?.results?.at(0)?.supports) {
      setSupportListUpdated(supportList.results.at(0)?.supports ?? []);
    } else {
      setSupportListUpdated([]);
    }
  }, [supportList.results, setSupportListUpdated]);

  const clientNit = companyList.results.find(
    (comp) => comp.id === Number(filters.corporateClient)
  )?.nit;

  const filterSection = () => {
    return (
      <>
        <div className="searchSection" data-testid="handleupdatesupportparameter-search-section">
          <Box>
            <Box className="flex gap-2">
              <Title
                pt={"xl"}
                mt={"sm"}
                pb={"xs"}
                px={"lg"}
                size={"h4"}
                w={"100%"}
                c="secondary-color"
                fw={"bold"}
                ta={"start"}
                className="flex justify-center"
              >
                Empresa cliente
              </Title>
            </Box>
            <Box px={"xl"}>
              <div className="cardSearch">
                <Select
                  data-testid="handleupdatesupportparameter-company-select"
                  clearable
                  disabled={action !== "create"}
                  label="Empresa cliente"
                  data={UseFormattedDataSelect(companyList.results, "name", "id")}
                  searchable
                  value={String(filters.corporateClient)}
                  placeholder="Seleccionar..."
                  onChange={(option) => {
                    setFilters((state) => ({
                      ...state,
                      corporateClient: option,
                      contractId: ""
                    }));
                    setSupportListUpdated([]);
                  }}
                />
                <TextInput
                  data-testid="handleupdatesupportparameter-nit-input"
                  label="NIT"
                  placeholder="Escribir..."
                  disabled
                  value={clientNit ?? ""}
                />
                <Select
                  data-testid="handleupdatesupportparameter-contract-select"
                  label="Contrato Dispensación"
                  disabled={action !== "create"}
                  data={UseFormattedDataSelect(contractList.results, "contractName", "id")}
                  searchable
                  value={String(filters.contractId)}
                  placeholder="Seleccionar..."
                  onChange={(_, value) => {
                    setFilters((state) => ({
                      ...state,
                      contractId: value?.value ?? ""
                    }));
                    setSupportListUpdated([]);
                  }}
                />
              </div>
            </Box>
          </Box>
        </div>
      </>
    );
  };

  const addSupport = () => {
    setSupportListUpdated((state) => [
      ...state,
      {
        filename: "",
        extensions: [],
        deletedAt: null,
        generateId: generateId()
      }
    ]);
  };

  const handleChangeSupport = (support: Support, field: string, value: string | string[]) => {
    const editSupport = supportListUpdated.map((item) =>
      (item.id ?? item.generateId) === (support.id ?? support.generateId)
        ? { ...item, [field]: value }
        : item
    );

    setSupportListUpdated(editSupport);
  };

  const valueMultiSelect = (support: Support) => {
    const isArrayOfJSON =
      Array.isArray(support.extensions) &&
      support.extensions.every((item) => typeof item === "object" && item !== null);

    if (support.id && isArrayOfJSON) {
      const valueMulti: string[] = (support.extensions as Extension[]).map((opt) =>
        opt?.id.toString()
      );
      return valueMulti as string[];
    }
    return support.extensions as string[];
  };
  const createSupportRequest = useCreateSupport(async () => {
    const doAfter = () => {
      setFilters((state) => ({
        ...state,
        trigger: (state.trigger ?? 0) + 1
      }));
    };

    await doAfter();
  });
  const updateSupportRequest = useUpdateSupport(async () => {
    const doAfter = () => {
      setFilters((state) => ({
        ...state,
        trigger: (state.trigger ?? 0) + 1
      }));
    };

    await doAfter();
  });

  const deleteSupport = (support: Support) => {
    if (!canDelete) {
      return;
    }
    const filter = supportListUpdated.filter((x) => x.generateId !== support.generateId);
    setSupportListUpdated(filter);
  };

  const onSubmit = async () => {
    if (!canCreate) {
      return;
    }
    const list = supportListUpdated.filter((item) => item.generateId);
    const validationFileName = list.some((item) => item.filename === "");
    const validationExtensions = list.some((item) => item.extensions.length === 0);

    if (validationFileName || validationExtensions) {
      warningNotification("Verifique que todos los campos se encuentren llenos");
    } else {
      await createSupportRequest.mutateAsync({
        contractId: Number(filters.contractId ?? ""),
        supports: list.map((x) => ({
          ...x,
          extensions: x.extensions.map(Number)
        }))
      });
    }
  };

  const handleEditFunction = async (support: Support, isEditing: boolean, isDisabled?: string) => {
    if (!isEditing && !canEdit) {
      return;
    }
    const editSupport = supportListUpdated.map((item) =>
      item.id === support.id ? { ...item, isEditing } : item
    );
    setSupportListUpdated(editSupport);

    if (!isEditing) {
      const list = supportListUpdated.find((item) => item.id === support.id);
      if (list) {
        await updateSupportRequest.mutateAsync({
          action: isDisabled ?? "",
          extensions: valueMultiSelect(support).map(Number),
          filename: list.filename,
          id: Number(support.id)
        });
      }
    }
  };

  const handleCancelFunction = (support: Support) => {
    const findSupport: Support = supportList.results
      .at(0)
      ?.supports.find((item) => item.id === support.id) ?? {
      filename: "",
      extensions: []
    };

    const editSupport: Support[] = supportListUpdated.map((item) =>
      item.id === support.id ? findSupport : item
    );
    if (editSupport) {
      setSupportListUpdated(editSupport);
    }
  };
  const supportListSection = () => {
    return (
      <Box w={"100%"}>
        <Title
          size={"h3"}
          w={"100%"}
          c="brand-color"
          pb={"sm"}
          fw={"bold"}
          ta={"start"}
          className="flex "
        >
          <div
            data-testid="handleupdatesupportparameter-back-button"
            className="flex items-center pointer"
            onClick={() => navigate(-1)}
          >
            <Tooltip label="Regresar" withArrow position="right">
              <IconChevronLeft
                style={{ height: 30, width: 30 }}
                className="primary-color pointer"
              />
            </Tooltip>
          </div>
          {action === "create" ? "Crear" : action === "detail" ? "Detalle de" : "Editar"}{" "}
          parametrización de soportes
        </Title>
        <Box py={"xs"}>
          <Grid>
            <Grid.Col span={10}>
              <b className="primary-color">Asesores</b>
            </Grid.Col>
            <Grid.Col span={2} className="flex justify-end">
              {action === "create" && (
                <Tooltip
                  label={canCreate ? "Agregar soporte" : RBAC_LABELS.NO_PERMISSION}
                  withArrow
                  position="left"
                >
                  <Button
                    variant="transparent"
                    data-testid="handleupdatesupportparameter-add-support-button"
                    disabled={!canCreate}
                    className={!canCreate ? "noPermissionDisabled" : ""}
                    rightSection={<IconCirclePlus />}
                    onClick={() => {
                      if (!canCreate) {
                        return;
                      }
                      addSupport();
                    }}
                  >
                    Agregar
                  </Button>
                </Tooltip>
              )}
            </Grid.Col>
          </Grid>
        </Box>

        {supportListUpdated.length > 0 &&
          supportListUpdated.map((support, index) => {
            const isDisabled = support.deletedAt !== null ? true : false;
            return (
              <Card
                mb={"sm"}
                key={support.filename}
                data-testid={`handleupdatesupportparameter-support-card-${index}`}
              >
                <Grid>
                  {support.id && (
                    <>
                      <Grid.Col span={10}>
                        {isDisabled ? (
                          <Box fz={12}>
                            <span>
                              Inhabilitado por&nbsp;
                              {formatSupportUser(support.deletedBy)} |{" "}
                              {formatUpdatedAt(support.deletedAt)}
                            </span>
                          </Box>
                        ) : (
                          <Box fz={12}>
                            {support.updatedBy !== "" && support.updatedBy !== null ? (
                              <div>
                                Editado por {formatSupportUser(support.updatedBy)} |{" "}
                                {formatUpdatedAt(support.updatedAt)}
                              </div>
                            ) : (
                              <div>
                                Creación: {formatUpdatedAt(support.createdAt)} |{" "}
                                {formatSupportUser(support.createdBy)}
                              </div>
                            )}
                          </Box>
                        )}
                      </Grid.Col>
                      <Grid.Col span={2} className="flex justify-end">
                        <Badge variant="light" color={isDisabled ? "red" : "green"}>
                          {isDisabled ? "Inhabilitado" : "Habilitado"}
                        </Badge>
                      </Grid.Col>
                    </>
                  )}
                  <Grid.Col span={5.5}>
                    <TextInput
                      label="Documento de cargue asesor"
                      placeholder="Escribir..."
                      data-testid={`handleupdatesupportparameter-filename-input-${index}`}
                      autoFocus
                      disabled={isDisabledField(support)}
                      defaultValue={support.filename}
                      onChange={({ target }) =>
                        handleChangeSupport(support, "filename", target.value)
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={5.5}>
                    <MultiSelect
                      clearable
                      data-testid={`handleupdatesupportparameter-extensions-select-${index}`}
                      label="Tipo de documento"
                      data={formattedDataSelect(
                        extensionList.results,
                        "extension",
                        "id",
                        [],
                        false
                      )}
                      searchable
                      disabled={isDisabledField(support)}
                      defaultValue={valueMultiSelect(support)}
                      onChange={(e) => handleChangeSupport(support, "extensions", e)}
                    />
                  </Grid.Col>
                  <Grid.Col span={1}>
                    {action !== "detail" && (
                      <Box
                        className="flex justify-center items-end full"
                        p={action === "create" ? "xs" : "0"}
                      >
                        {action === "create" ? (
                          <div
                            className={`pointer${!canDelete ? " noPermissionDisabled" : ""}`}
                            data-testid={`handleupdatesupportparameter-delete-button-${index}`}
                            onClick={() => {
                              if (!canDelete) {
                                return;
                              }
                              deleteSupport(support);
                            }}
                          >
                            {!isDisabledField(support) && (
                              <Tooltip
                                label={canDelete ? "Eliminar" : RBAC_LABELS.NO_PERMISSION}
                                withArrow
                                position="left"
                              >
                                <IconTrash color={!canDelete ? "gray" : undefined} />
                              </Tooltip>
                            )}
                          </div>
                        ) : support.isEditing ? (
                          <Box pb={"0.2rem"} className="flex justify-around">
                            <Tooltip label={canEdit ? "Guardar" : RBAC_LABELS.NO_PERMISSION}>
                              <IconCircleCheck
                                color={canEdit ? "#7bc62d" : "gray"}
                                style={{ width: 25, height: 25 }}
                                data-testid={`handleupdatesupportparameter-save-button-${index}`}
                                className={`pointer${!canEdit ? " noPermissionDisabled" : ""}`}
                                onClick={() => {
                                  if (!canEdit) {
                                    return;
                                  }
                                  handleEditFunction(support, false, "");
                                }}
                              />
                            </Tooltip>
                            <Tooltip label={"Cancelar"}>
                              <IconCircleX
                                color="#ff2825"
                                style={{
                                  width: 25,
                                  height: 25
                                }}
                                data-testid={`handleupdatesupportparameter-cancel-button-${index}`}
                                className="pointer"
                                onClick={() => handleCancelFunction(support)}
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Menu
                            position="bottom-end"
                            withArrow
                            data-testid={`handleupdatesupportparameter-menu-${index}`}
                          >
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                data-testid={`handleupdatesupportparameter-menu-button-${index}`}
                              >
                                <IconDotsVertical />
                              </ActionIcon>
                            </Menu.Target>

                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconPencil />}
                                disabled={!canEdit}
                                data-testid={`handleupdatesupportparameter-edit-item-${index}`}
                                onClick={() => {
                                  if (!canEdit) {
                                    return;
                                  }
                                  handleEditFunction(support, true);
                                }}
                              >
                                {!canEdit ? RBAC_LABELS.NO_PERMISSION : "Editar"}
                              </Menu.Item>
                              <Menu.Item
                                color={isDisabled ? "green" : "red"}
                                disabled={!canEdit}
                                leftSection={<IconCircleFilled style={{ width: 10 }} />}
                                data-testid={`handleupdatesupportparameter-status-item-${index}`}
                                onClick={() => {
                                  if (!canEdit) {
                                    return;
                                  }
                                  confirmNotification(
                                    `Se ${
                                      isDisabled ? "habilitará" : "inhabilitará"
                                    } el soporte: ${support.filename}`,
                                    "¿Está seguro?",
                                    () => {
                                      handleEditFunction(
                                        support,
                                        false,
                                        isDisabled ? "enabled" : "disabled"
                                      );
                                    }
                                  );
                                }}
                              >
                                {!canEdit
                                  ? RBAC_LABELS.NO_PERMISSION
                                  : isDisabled
                                    ? "Habilitar"
                                    : "Inhabilitar"}
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        )}
                      </Box>
                    )}
                  </Grid.Col>
                </Grid>
              </Card>
            );
          })}
      </Box>
    );
  };
  const renderComponent = () => {
    return (
      <Fragment>
        <div className="bodyOrder" data-testid="handleupdatesupportparameter-main-container">
          {filterSection()}
          <Box
            className="renderSection main-container"
            data-testid="handleupdatesupportparameter-render-section"
          >
            {filters.contractId && filters.corporateClient && (
              <>
                {supportListSection()}
                <Box className="flex justify-end gap-3" w={"100%"}>
                  <Button
                    variant="outline"
                    data-testid="handleupdatesupportparameter-cancel-button"
                    onClick={() => navigate(-1)}
                  >
                    Cancelar
                  </Button>
                  {action === "create" &&
                    supportListUpdated.filter((item) => item.generateId).length > 0 && (
                      <Tooltip
                        label={canCreate ? "Guardar parametrización" : RBAC_LABELS.NO_PERMISSION}
                        withArrow
                      >
                        <Button
                          variant="filled"
                          data-testid="handleupdatesupportparameter-save-button"
                          disabled={!canCreate}
                          className={!canCreate ? "noPermissionDisabled" : ""}
                          onClick={() => {
                            if (!canCreate) {
                              return;
                            }
                            onSubmit();
                          }}
                        >
                          Guardar
                        </Button>
                      </Tooltip>
                    )}
                </Box>
              </>
            )}
          </Box>
        </div>
      </Fragment>
    );
  };

  return renderComponent();
};

export default HandleUpdateSupportParameter;
